import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EarningsTab = ({ token }) => {
  const [earnings, setEarnings] = useState(null);
  const [chartView, setChartView] = useState('daily');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/earnings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEarnings(r.data))
      .catch(() => toast.error('Failed to load earnings'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  if (!earnings) return null;

  const chartData = chartView === 'daily' ? earnings.daily_chart : earnings.monthly_chart;
  const maxRevenue = chartData.length > 0 ? Math.max(...chartData.map(d => d.revenue), 1) : 1;
  const maxOrders = chartData.length > 0 ? Math.max(...chartData.map(d => d.orders), 1) : 1;

  const periodCards = [
    { label: 'Today', data: earnings.day, color: 'bg-blue-600' },
    { label: 'This Week', data: earnings.week, color: 'bg-green-600' },
    { label: 'This Month', data: earnings.month, color: 'bg-purple-600' },
    { label: 'This Year', data: earnings.year, color: 'bg-orange-600' },
  ];

  return (
    <div className="space-y-5" data-testid="admin-earnings">
      {/* Period Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {periodCards.map(p => (
          <div key={p.label} className="bg-white border border-gray-200 rounded p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{p.label}</p>
            <p className="text-3xl font-black text-gray-900" data-testid={`earnings-${p.label.replace(/\s/g, '-').toLowerCase()}`}>
              ${p.data.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-500 mt-1">{p.data.orders} order{p.data.orders !== 1 ? 's' : ''}</p>
          </div>
        ))}
      </div>

      {/* All-Time */}
      <div className="bg-gray-900 rounded p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">All-Time Revenue</p>
            <p className="text-4xl font-black mt-1" data-testid="earnings-all-time">
              ${earnings.all_time.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Total Orders</p>
            <p className="text-4xl font-black mt-1">{earnings.all_time.orders.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Revenue & Orders</h3>
          <div className="flex bg-gray-100 rounded p-0.5">
            <button onClick={() => setChartView('daily')}
              className={`px-3 py-1 text-xs font-bold uppercase rounded transition-colors ${chartView === 'daily' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="chart-daily">Daily</button>
            <button onClick={() => setChartView('monthly')}
              className={`px-3 py-1 text-xs font-bold uppercase rounded transition-colors ${chartView === 'monthly' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="chart-monthly">Monthly</button>
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
            No order data yet. Revenue chart will appear when orders come in.
          </div>
        ) : (
          <div className="space-y-2">
            {/* Revenue Bars */}
            <p className="text-xs text-gray-500 font-semibold uppercase">Revenue</p>
            <div className="flex items-end gap-1 h-40" data-testid="revenue-chart">
              {chartData.map((d, i) => {
                const h = (d.revenue / maxRevenue) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                    <div className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600 cursor-pointer min-h-[2px]"
                      style={{ height: `${Math.max(h, 1)}%` }} />
                    <span className="text-[9px] text-gray-400 truncate max-w-full">{(d.date || d.month || '').slice(-5)}</span>
                    <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      ${d.revenue.toFixed(2)} · {d.orders} orders
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Orders Bars */}
            <p className="text-xs text-gray-500 font-semibold uppercase mt-4">Orders</p>
            <div className="flex items-end gap-1 h-24" data-testid="orders-chart">
              {chartData.map((d, i) => {
                const h = (d.orders / maxOrders) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full bg-green-500 rounded-t transition-all hover:bg-green-600 min-h-[2px]"
                      style={{ height: `${Math.max(h, 1)}%` }} />
                    <span className="text-[9px] text-gray-400 truncate max-w-full">{(d.date || d.month || '').slice(-5)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default EarningsTab;
